import { Injectable } from '@angular/core';
import { getFormattedYearMonthDate } from '../../../../shared/util/date-helper';
import {
  CheckBoxInputData,
  FileAndTextInputData,
  FileInputData,
  ListInputData,
  RadioButtonInputData,
  SelfInspcetionWrapperData,
  TextInputData,
} from '../../report-inputs/report-input-entities';
import { RolloutProgressReportEntities } from '../entities';

@Injectable({
  providedIn: 'root',
})
export class RolloutProgressReportHelperService {
  convertAnswerV2 = (
    selfInspection: RolloutProgressReportEntities.v3.CombinedSelfInspections,
    language: keyof RolloutProgressReportEntities.v3.IdentifierObject,
  ) => {
    const definitionWithAnswer = {
      ...selfInspection.definition,
      answer: selfInspection.answer,
    };

    return this.convertAnswer(
      selfInspection.definition.type,
      definitionWithAnswer,
      language,
    );
  };

  createWrapperDataV2 = (
    selfInspection: RolloutProgressReportEntities.v3.CombinedSelfInspections,
    language: keyof RolloutProgressReportEntities.v3.IdentifierObject,
  ): SelfInspcetionWrapperData => {
    const definitionWithAnswer = {
      ...selfInspection.definition,
      answer: selfInspection.answer,
    };
    return this.createWrapperData(definitionWithAnswer, language);
  };

  convertAnswer = (
    definitionType: RolloutProgressReportEntities.v3.DefinitionType,
    definitionWithAnswer: RolloutProgressReportEntities.v3.DefinitionWithAnswer,
    language: keyof RolloutProgressReportEntities.v3.IdentifierObject,
  ) => {
    switch (definitionType) {
      case RolloutProgressReportEntities.v3.DefinitionType.TEXT:
        return this.createTextData(definitionWithAnswer, language);
      case RolloutProgressReportEntities.v3.DefinitionType.CHECKBOX:
        return this.createCheckboxData(definitionWithAnswer, language);
      case RolloutProgressReportEntities.v3.DefinitionType.FILES:
        return this.createFileData(definitionWithAnswer, language);
      case RolloutProgressReportEntities.v3.DefinitionType.FILE_AND_TEXT:
        return this.createFileAndTextData(definitionWithAnswer, language);
      case RolloutProgressReportEntities.v3.DefinitionType.LIST:
        return this.createListData(definitionWithAnswer, language);
      case RolloutProgressReportEntities.v3.DefinitionType.RADIOBUTTON:
        return this.createRadioButtonData(definitionWithAnswer, language);
      case RolloutProgressReportEntities.v3.DefinitionType.TEXT_AREA:
        return this.createTextData(definitionWithAnswer, language, false, true);
    }
  };

  createWrapperData = (
    definitionWithAnswer: RolloutProgressReportEntities.v3.DefinitionWithAnswer,
    language: keyof RolloutProgressReportEntities.v3.IdentifierObject,
  ): SelfInspcetionWrapperData => ({
    id: definitionWithAnswer.id,
    completed: !!definitionWithAnswer.answer?.completed,
    updatedBy: definitionWithAnswer.answer?.updatedBy,
    lastUpdateAt: definitionWithAnswer.answer
      ? (getFormattedYearMonthDate(
          definitionWithAnswer.answer.lastUpdateAt.toString(),
        ) ?? '')
      : '',
    isNotApplicableComment:
      definitionWithAnswer.answer?.isNotApplicableComment ?? '',
    subSection: definitionWithAnswer.subSection[language] ?? '',
    description: definitionWithAnswer.description[language] ?? '',
    instructionalImage: definitionWithAnswer.instructionalImage,
  });

  createTextData = (
    definitionWithAnswer: RolloutProgressReportEntities.v3.DefinitionWithAnswer,
    language: keyof RolloutProgressReportEntities.v3.IdentifierObject,
    isFileAndText?: boolean,
    isTextArea?: boolean,
  ): TextInputData => ({
    id: definitionWithAnswer.id,
    isTextArea: isTextArea ?? false,
    isDone: !!definitionWithAnswer.answer?.completed,
    label: definitionWithAnswer.description[language],
    placeHolder: isFileAndText
      ? (definitionWithAnswer.fileAndText?.text.placeholder ?? '')
      : (definitionWithAnswer.text?.placeholder ?? ''),
    savedValue: isFileAndText
      ? (definitionWithAnswer.answer?.fileAndText?.text ?? '')
      : (definitionWithAnswer.answer?.text ?? ''),
  });

  createRadioButtonData = (
    definitionWithAnswer: RolloutProgressReportEntities.v3.DefinitionWithAnswer,
    language: keyof RolloutProgressReportEntities.v3.IdentifierObject,
  ): RadioButtonInputData => ({
    id: definitionWithAnswer.id,
    radioButtonGroupId: definitionWithAnswer.id,
    label: definitionWithAnswer.description[language],
    isDone: !!definitionWithAnswer.answer?.completed,
    radioButtons: definitionWithAnswer.radioButton
      ? definitionWithAnswer.radioButton.alternatives.map((button) => ({
          label: button.label[language],
          value: button.value,
          isChecked: button.value === definitionWithAnswer.answer?.radioButton,
        }))
      : [],
  });

  createCheckboxData = (
    definitionWithAnswer: RolloutProgressReportEntities.v3.DefinitionWithAnswer,
    language: keyof RolloutProgressReportEntities.v3.IdentifierObject,
  ): CheckBoxInputData => ({
    id: definitionWithAnswer.id,
    isDone: !!definitionWithAnswer.answer?.completed,
    label: definitionWithAnswer.description[language],
  });

  createListData = (
    definitionWithAnswer: RolloutProgressReportEntities.v3.DefinitionWithAnswer,
    language: keyof RolloutProgressReportEntities.v3.IdentifierObject,
  ): ListInputData => ({
    id: definitionWithAnswer.id,
    isDone: !!definitionWithAnswer.answer?.completed,
    label: definitionWithAnswer.description[language],
    placeHolder: definitionWithAnswer.list?.placeholder ?? '',
    requiredFieldsAmount: 1,
    values: definitionWithAnswer.answer?.list ?? [],
    icon: 'circleQuestion',
  });

  createFileData = (
    definitionWithAnswer: RolloutProgressReportEntities.v3.DefinitionWithAnswer,
    language: keyof RolloutProgressReportEntities.v3.IdentifierObject,
  ): FileInputData => ({
    id: definitionWithAnswer.id,
    isDone: !!definitionWithAnswer.answer?.completed,
    label: definitionWithAnswer.description[language],
    fileData: {
      type: definitionWithAnswer.file?.isImages ? 'IMAGE' : 'FILE',
      files: definitionWithAnswer.answer?.files?.map((file) => file),
      mimeType: definitionWithAnswer.file?.mimeType ?? '',
      imageAmount: definitionWithAnswer.answer?.files?.length,
    },
  });

  createFileAndTextData = (
    definitionWithAnswer: RolloutProgressReportEntities.v3.DefinitionWithAnswer,
    language: keyof RolloutProgressReportEntities.v3.IdentifierObject,
  ): FileAndTextInputData => ({
    id: definitionWithAnswer.id,
    isDone: !!definitionWithAnswer.answer?.completed,
    label: definitionWithAnswer.description[language],
    fileData: {
      type: definitionWithAnswer.fileAndText?.file.isImages ? 'IMAGE' : 'FILE',
      mimeType: definitionWithAnswer.fileAndText?.file.mimeType ?? '',
      imageAmount: definitionWithAnswer.answer?.fileAndText?.fileMetas?.length,
      files:
        definitionWithAnswer.answer?.fileAndText?.fileMetas?.map(
          (file) => file,
        ) ?? [],
    },
    textData: this.createTextData(definitionWithAnswer, language, true),
  });

  createCompleteInfoString = (
    definitionWithAnswer: RolloutProgressReportEntities.v3.DefinitionWithAnswer,
  ): string =>
    definitionWithAnswer.answer
      ? `${getFormattedYearMonthDate(
          definitionWithAnswer.answer.lastUpdateAt.toString(),
        )} ${definitionWithAnswer.answer.updatedBy}`
      : '';
}
